/**
 * DiscoverCardWrappers.js
 *
 * Thin wrappers that handle data fetching for collections and boards,
 * then delegate rendering to UnifiedCard.
 *
 * UnifiedCard is a pure display component, so these wrappers bridge the gap
 * by fetching preview data and normalizing it before rendering.
 *
 * Both wrappers are memo()'d with useMemo'd normalization + actions — the
 * same pattern as FeedCard, and for the same reason: without it, any parent
 * state change (a like toggle, a keystroke in a page-level input) re-runs
 * normalizeCardData and re-renders every mounted card. For memo() to hold,
 * call sites must pass stable handler props (the useFeedModals handlers are
 * useCallback-stable; don't wrap them in inline arrows).
 */

import React, { useState, useEffect, useMemo, memo } from 'react';
import UnifiedCard from '../UnifiedCard';
import { normalizeCardData, CONTENT_TYPES, CARD_CONTEXTS } from '../../utils/normalizeCardData';
import SocialServiceDB from '../../services/socialServiceDB';
import TravelBoardsServiceDB from '../../services/travelBoardsServiceDB';

/**
 * DiscoverCollectionCard
 *
 * Fetches collection preview activities, normalizes the data, and renders UnifiedCard.
 *
 * Props:
 * @param {Object} collection - Raw collection data from social feed
 * @param {Function} onLike - Like handler
 * @param {Function} onComment - Comment handler
 * @param {Function} onSave - Save handler
 * @param {Function} onShare - Share handler
 * @param {Function} onCardClick - Optional card click override
 * @param {string} className - Additional CSS classes
 */
export const DiscoverCollectionCard = memo(function DiscoverCollectionCard({
  collection,
  onLike,
  onComment,
  onSave,
  onShare,
  onNotInterested,
  onReport,
  onBlockUser,
  onCardClick,
  className = '',
  imageSizing,
  // Discover only: one-tap Follow on a stranger's post.
  showFollow = false
}) {
  const [previewActivities, setPreviewActivities] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPreview = async () => {
      if (!collection?.id) return;

      // Feed payloads embed preview_activities (batched server-side, like
      // boards' preview_items) — skip the per-card API call when present.
      // [] is a valid embedded value (empty collection), so check the type.
      if (Array.isArray(collection.preview_activities)) {
        setPreviewActivities(collection.preview_activities);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const result = await SocialServiceDB.getCollectionPreview(collection.id);
        if (result.success) {
          setPreviewActivities(result.preview_activities || []);
        } else {
          setPreviewActivities([]);
        }
      } catch (error) {
        console.error('Error fetching collection preview:', error);
        setPreviewActivities([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreview();
  }, [collection?.id, collection?.preview_activities]);

  const normalizedData = useMemo(() => {
    // Merge preview activities into collection data for normalization
    const enrichedCollection = previewActivities !== null
      ? { ...collection, preview_activities: previewActivities }
      : collection;
    return normalizeCardData(enrichedCollection, CONTENT_TYPES.COLLECTION, CARD_CONTEXTS.DISCOVER);
  }, [collection, previewActivities]);

  const actions = useMemo(() => ({
    onLike: onLike ? () => onLike(collection) : undefined,
    onComment: onComment ? () => onComment(collection) : undefined,
    onSave: onSave ? () => onSave(collection) : undefined,
    onShare: onShare ? () => onShare(collection) : undefined,
    onNotInterested: onNotInterested ? () => onNotInterested(collection) : undefined,
    onReport: onReport ? () => onReport(collection) : undefined,
    onBlockUser: onBlockUser ? () => onBlockUser(collection) : undefined
  }), [collection, onLike, onComment, onSave, onShare, onNotInterested, onReport, onBlockUser]);

  return (
    <UnifiedCard
      data={normalizedData}
      isLoading={isLoading}
      actions={actions}
      onCardClick={onCardClick}
      showCreator={true}
      showFollow={showFollow}
      className={className}
      imageSizing={imageSizing}
    />
  );
});

/**
 * DiscoverBoardCard
 *
 * Uses preview_items from social feed data, or fetches from API.
 * Normalizes the data and renders UnifiedCard.
 *
 * Props:
 * @param {Object} board - Raw board data from social feed
 * @param {Function} onLike - Like handler
 * @param {Function} onComment - Comment handler
 * @param {Function} onSave - Save handler
 * @param {Function} onShare - Share handler
 * @param {Function} onCardClick - Optional card click override
 * @param {string} className - Additional CSS classes
 */
export const DiscoverBoardCard = memo(function DiscoverBoardCard({
  board,
  onLike,
  onComment,
  onSave,
  onShare,
  onNotInterested,
  onReport,
  onBlockUser,
  onCardClick,
  className = '',
  imageSizing,
  // Discover only: one-tap Follow on a stranger's post.
  showFollow = false
}) {
  const [previewItems, setPreviewItems] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPreview = async () => {
      if (!board?.id) return;

      // The feed or profile card's preview_items, when it brought any. An
      // empty array is an answer — the listing gate left nothing to show —
      // not "not loaded": refetching would ask again and show what it
      // filtered out (C347).
      if (Array.isArray(board.preview_items)) {
        const transformedItems = board.preview_items.map(item => ({
          content_type: item.type || item.content_type,
          title: item.title || item.item_title,
          cover_photo: item.image || item.preview_image || item.cover_photo
        }));
        setPreviewItems(transformedItems);
        setIsLoading(false);
        return;
      }

      // Nothing brought: ask, through the same listing gate as the feed's
      // own preview (a card amplifies; the board's page is reach).
      setIsLoading(true);
      try {
        const result = await TravelBoardsServiceDB.getBoardPreview(board.id, { surface: 'list' });
        if (result.success) {
          setPreviewItems(result.preview_items || []);
        } else {
          setPreviewItems([]);
        }
      } catch (error) {
        console.error('Error fetching board preview:', error);
        setPreviewItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreview();
  }, [board?.id, board?.preview_items]);

  const normalizedData = useMemo(() => {
    // Merge preview items into board data for normalization
    const enrichedBoard = previewItems !== null
      ? { ...board, preview_items: previewItems }
      : board;
    return normalizeCardData(enrichedBoard, CONTENT_TYPES.BOARD, CARD_CONTEXTS.DISCOVER);
  }, [board, previewItems]);

  const actions = useMemo(() => ({
    onLike: onLike ? () => onLike(board) : undefined,
    onComment: onComment ? () => onComment(board) : undefined,
    onSave: onSave ? () => onSave(board) : undefined,
    onShare: onShare ? () => onShare(board) : undefined,
    onNotInterested: onNotInterested ? () => onNotInterested(board) : undefined,
    onReport: onReport ? () => onReport(board) : undefined,
    onBlockUser: onBlockUser ? () => onBlockUser(board) : undefined
  }), [board, onLike, onComment, onSave, onShare, onNotInterested, onReport, onBlockUser]);

  return (
    <UnifiedCard
      data={normalizedData}
      isLoading={isLoading}
      actions={actions}
      onCardClick={onCardClick}
      showCreator={true}
      showFollow={showFollow}
      className={className}
      imageSizing={imageSizing}
    />
  );
});
